<template>
  <div ref="videosRef" :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="videos-content">
        <aside class="videos-content-filter">
          <div class="videos-content-filter-title">
            <JssRichText :field="fields?.filterLabel" />
          </div>
          <div class="videos-content-filter-body">
            <DynamicForm :form="fields?.filterForm" @change="onFilterChange" />
          </div>
        </aside>
        <div class="videos-content-list">
          <div class="videos-content-list-toolbar">
            <FormInput
              class="videos-content-list-search"
              :value="searchKeyword"
              placeholder="Search"
              data-name="videos-search"
              @change="onSearchInput"
              @keydown.enter="onSearchSubmit"
            >
              <template #icon>
                <button
                  class="videos-content-list-search-btn"
                  type="button"
                  @click="onSearchSubmit"
                >
                  <svg
                    class="videos-content-list-search-icon"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      d="M7.2 12.4A5.2 5.2 0 1 1 7.2 2a5.2 5.2 0 0 1 0 10.4Zm0-1.2a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm3.7.55.85-.85 2.75 2.75-.85.85-2.75-2.75Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </template>
            </FormInput>
          </div>
          <div class="videos-content-list-items">
            <site-link
              v-for="item in videosList"
              :key="item.id"
              class="videos-content-list-item"
              :link="item.fields?.url"
              target="_blank"
            >
              <AdaptiveImage
                class="videos-content-list-item-image"
                :field="{ fields: item?.fields?.image?.value }"
                :lazy-load="false"
                :enableScrollDetection="false"
                :show="true"
                :is-background="false"
              />
              <div class="videos-content-list-item-text">
                <div>
                  <JssRichText
                    class="videos-content-list-item-title"
                    :field="item?.fields?.title"
                  />
                  <JssRichText
                    class="videos-content-list-item-subtitle"
                    :field="item?.fields?.subtitle"
                  />
                </div>
                <div class="videos-content-list-item-file">
                  <div class="videos-content-list-item-file-content">
                    <JssRichText
                      class="videos-content-list-item-categories"
                      :field="item?.fields?.categories?.value?.[0].fields?.text"
                    />
                    <div
                      class="videos-content-list-item-file-info"
                      v-if="item?.fields?.file?.value"
                    >
                      <span class="videos-content-list-item-file-type">
                        {{ item?.fields?.file?.value?.extension?.toUpperCase() }}
                      </span>
                      <span class="videos-content-list-item-file-sep">·</span>
                      <span class="videos-content-list-item-file-size">
                        {{ formatFileSize(item?.fields?.file?.value?.size) }}
                      </span>
                    </div>
                  </div>
                  <SiteButton v-bind="fields?.button" />
                </div>
              </div>
            </site-link>
          </div>
          <div class="videos-content-nodata" v-if="showNodata">
            <JssRichText :field="fields?.noDataText" />
          </div>
          <Pagination
            v-model:currentPage="currentPage"
            :totalPages="totalPages"
            v-if="totalPages > 1"
          />
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import utils from '@/utils';
import api from '@/api';
import FormInput from '@/elements/FormInput.vue';

export default {
  name: 'Videos',
  components: { FormInput },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    page: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const videosRef = ref(null);
    const searchKeyword = ref('');
    const videosList = ref([]);
    const isLoading = ref(false);
    const currentPage = ref(1);
    const totalCount = ref(0);
    const filterValues = ref({});
    let loadTimer = null;
    let latestRequestId = 0;

    const pageSize = computed(() => {
      const num = Number(props.fields?.pageSize?.value);
      return Number.isFinite(num) && num > 0 ? num : 10;
    });

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'videos'));

    const rootStyle = computed(() => utils.overall.baseComStyle(props.fields));

    const totalPages = computed(() => {
      const pages = Math.ceil(totalCount.value / pageSize.value);
      return pages > 0 ? pages : 1;
    });

    const showNodata = computed(() => !isLoading.value && videosList.value.length === 0);

    const formatFileSize = (bytes) => {
      const num = Number(bytes);
      if (!Number.isFinite(num) || num <= 0) return '';
      if (num < 1024) return `${num} B`;
      if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
      return `${(num / (1024 * 1024)).toFixed(1)} MB`;
    };

    const loadVideos = async () => {
      const requestId = ++latestRequestId;
      isLoading.value = true;
      try {
        const params = {
          keyword: searchKeyword.value.trim(),
          page: currentPage.value,
          size: pageSize.value,
        };
        for (const [key, values] of Object.entries(filterValues.value)) {
          const arr = Array.isArray(values) ? values : [values];
          if (arr.length > 0 && !arr.some((v) => /^all/i.test(v))) {
            const paramKey = key.charAt(0).toLowerCase() + key.slice(1);
            params[paramKey === 'categories' ? 'category' : paramKey] = arr.join(',');
          }
        }
        const [res, ex] = await api.videos.getVideos(params);
        if (requestId !== latestRequestId) return;
        const data = res || ex || {};
        videosList.value = data?.list || [];
        totalCount.value = Number(data?.total) || 0;
      } catch (error) {
        if (requestId !== latestRequestId) return;
        videosList.value = [];
        totalCount.value = 0;
      } finally {
        if (requestId === latestRequestId) {
          isLoading.value = false;
        }
      }
    };

    const debounceLoadVideos = () => {
      if (loadTimer) clearTimeout(loadTimer);
      latestRequestId += 1;
      loadTimer = setTimeout(() => {
        loadTimer = null;
        if (currentPage.value === 1) {
          loadVideos();
        } else {
          currentPage.value = 1;
        }
      }, 500);
    };

    const onSearchInput = (_event, value) => {
      searchKeyword.value = value;
    };

    const onSearchSubmit = (event) => {
      // Ignore Enter during IME composition (e.g. confirming pinyin candidates)
      if (event?.isComposing) return;
      if (currentPage.value === 1) {
        loadVideos();
      } else {
        currentPage.value = 1;
      }
    };

    const onFilterChange = (_fieldType, fieldName, value) => {
      filterValues.value[fieldName] = Array.isArray(value) ? value : [value];
      debounceLoadVideos();
    };

    watch(currentPage, loadVideos);

    onMounted(() => {
      loadVideos();
    });

    return {
      rootClass,
      rootStyle,
      totalPages,
      showNodata,
      formatFileSize,
      searchKeyword,
      onSearchInput,
      onSearchSubmit,
      onFilterChange,
      videosList,
      isLoading,
      currentPage,
      totalCount,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.videos {
  position: relative;
  width: 100%;
  padding: 40px 0;
  z-index: 2;

  .f-check-list__header {
    min-height: 22px;
    cursor: default;
    pointer-events: none;
  }

  .f-check-list__expander {
    display: none !important;
  }

  // Force FormCheckList content always visible (override v-show)
  .f-check-list > div:not(.f-check-list__header) {
    display: block !important;
  }

  .f-check-list__label {
    color: $black;
    font-size: 16px;
    font-weight: 600;
  }

  .f-check-list__item {
    gap: 8px;
  }

  .e-form-checkbox__check {
    border-radius: 4px;
  }

  .e-form-checkbox.checked .e-form-checkbox__check {
    border-color: $blue-night;
  }

  &-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;

    &-nodata {
      padding: 32px;
      color: #3e3e3e;
      background: #f7f8f7;
      text-align: center;
      font-size: 20px;
    }
  }

  &-content-filter {
    width: 100%;
    flex-shrink: 0;

    &-title {
      font-size: 24px;
      font-weight: 600;
      color: $color-black;
      line-height: 1.4;
      margin-bottom: 24px;
    }

    &-body {
      padding-bottom: 16px;
    }
  }

  &-content-list {
    flex: 1;
    min-width: 0;

    &-toolbar {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 22px;
    }

    &-search {
      width: min(100%, 240px);
    }

    &-search .e-form-input__main-input {
      height: 32px;
      border: 0;
      border-radius: 4px;
      background: #f4f4f4;
      padding: 0 36px 0 14px;
    }

    &-search .e-form-input__input {
      position: relative;
      z-index: 2;
    }

    &-search .e-form-input__messages,
    &-search .e-form-input__title {
      display: none;
    }

    &-search .e-form-input__icon {
      right: 12px;
      color: #7da98b;
    }

    &-search-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: inherit;
    }

    &-search-icon {
      width: 18px;
      height: 18px;
      display: block;
    }

    &-items {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &-item {
      flex: 1;
      display: flex;
      gap: 24px;

      &-image {
        width: 130px;
        aspect-ratio: 13 / 18;
      }

      &-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      &-title {
        font-size: 18px;
        font-weight: 600;
        color: $black;
        line-height: 1.4;
      }

      &-subtitle {
        font-size: 14px;
        font-weight: 400;
        color: $black;
        line-height: 1.4;
        margin-top: 8px;
      }

      &-file {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 5px;

        &-content {
          display: flex;
          gap: 5px;
        }

        &-info {
          display: flex;
          align-items: center;
          gap: 3px;
        }

        &-sep {
          color: #999;
        }

        &-type,
        &-size {
          font-size: 14px;
          color: #999;
          line-height: 1.4;
        }
      }

      &-categories {
        font-size: 14px;
        color: #656565;
        line-height: 1.4;
        text-transform: capitalize;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-content {
      flex-direction: row;
      align-items: flex-start;
      gap: 32px;
    }

    &-content-filter {
      flex: 0 0 240px;
      width: 240px;

      // Override FormCheckList's horizontal 2-column layout for narrow sidebar
      .f-check-list {
        &__items {
          flex-direction: column;
          flex-wrap: nowrap;
        }
        &__item {
          flex: 0 0 100%;
        }
        &__des {
          margin-bottom: 16px;
        }
      }
    }
    &-content-list {
      &-item {
        &-image {
          width: 244px;
          aspect-ratio: 244 / 137;
        }

        &-file {
          flex-direction: row;
        }
      }
    }
  }
}
</style>
